import { useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import type { SignatureStrokes } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Button } from '../../components/ui';

const WIDTH = 400;
const HEIGHT = 160;
const MAX_STROKES = 200;
const MAX_POINTS = 5000;
const round = (n: number): number => Math.round(n * 10) / 10;

/**
 * Draws a signature as vectors (x, y, time) and hands the strokes to the parent. Unlike the portal's pad it uploads
 * nothing itself: the strokes travel with the notice acceptance (POST .../notices), so no portal endpoint is involved.
 */
export function StrokePad({ onChange }: { onChange: (s: SignatureStrokes | null) => void }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const strokes = useRef<[number, number, number][][]>([]);
  const started = useRef(0);
  const drawing = useRef(false);
  const [count, setCount] = useState(0);

  const ctx = () => {
    try {
      return canvas.current?.getContext('2d') ?? null; // jsdom has none; the strokes are still recorded
    } catch {
      return null;
    }
  };
  const point = (e: PointerEvent<HTMLCanvasElement>): [number, number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = rect.width > 0 ? WIDTH / rect.width : 1;
    const sy = rect.height > 0 ? HEIGHT / rect.height : 1;
    return [round((e.clientX - rect.left) * sx), round((e.clientY - rect.top) * sy), Math.max(0, Math.round(performance.now() - started.current))];
  };
  const publish = () => onChange(strokes.current.length > 0 ? { v: 1, width: WIDTH, height: HEIGHT, strokes: strokes.current } : null);

  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    if (strokes.current.length >= MAX_STROKES) return;
    if (strokes.current.length === 0) started.current = performance.now();
    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = point(e);
    strokes.current.push([p]);
    setCount(strokes.current.length);
    const c = ctx();
    if (c) {
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(p[0], p[1]);
    }
    publish();
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const s = strokes.current[strokes.current.length - 1];
    if (!s || s.length >= MAX_POINTS) return;
    const p = point(e);
    s.push(p);
    const c = ctx();
    if (c) {
      c.lineTo(p[0], p[1]);
      c.stroke();
    }
  };
  const up = () => {
    drawing.current = false;
  };
  const clear = () => {
    strokes.current = [];
    setCount(0);
    ctx()?.clearRect(0, 0, WIDTH, HEIGHT);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvas}
        width={WIDTH}
        height={HEIGHT}
        aria-label={t('publicLinks.pre.sign.canvas')}
        className="w-full touch-none rounded-md border border-slate-300 bg-white"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
      />
      <Button variant="secondary" disabled={count === 0} onClick={clear}>
        {t('publicLinks.pre.sign.clear')}
      </Button>
    </div>
  );
}
